import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import {
  BidResponseModel,
  BidResponseModelList,
  ContractResponse,
  FixedBidResponse,
  Trade,
} from "../interfaces/responses/models";
import {
  FilterBidsRequest,
  FixedRateBidRequest,
} from "../interfaces/requests/models";
import { BidResponse } from "../interfaces/responses/models";

@Injectable({
  providedIn: "root",
})
export class BidService {
  private apiUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  private createCompleteUrl(path: string): string {
    return `${this.apiUrl}${path}`;
  }

  createBid(data: any): Observable<any> {
    return this.http.post<any>(this.createCompleteUrl("bids/"), data);
  }

  activateBid(bidId: string): Observable<any> {
    return this.http.put<any>(
      this.createCompleteUrl(`bids/${bidId}/activate`),
      {}
    );
  }

  getBidTypes(): Observable<any> {
    return this.http.get<any>(this.createCompleteUrl("bids/types"));
  }
  getTransactionTypes(): Observable<any> {
    return this.http.get<any>(this.createCompleteUrl("transactions/types"));
  }
  getFilteredBids(data?: FilterBidsRequest): Observable<any> {
    return this.http.get<any>(this.createCompleteUrl(`bids/`), {
      params: { ...data },
    });
  }

  getBidById(bidId: string): Observable<BidResponseModel> {
    return this.http.get<BidResponseModel>(
      this.createCompleteUrl(`bids/${bidId}`)
    );
  }

  cancelBid(bidId: string): Observable<any> {
    return this.http.delete<any>(
      this.createCompleteUrl(`bids/${bidId}/cancel`)
    );
  }

  acceptBid(data: any): Observable<any> {
    let bidId = data.bid_id;
    return this.http.put<any>(
      this.createCompleteUrl(`bids/${bidId}/accept`),
      data
    );
  }

  updateBid(data: any): Observable<any> {
    let bidId = data.bid_id;
    let complete_url = this.createCompleteUrl(`bids/${bidId}/update`);
    return this.http.put<any>(complete_url, data);
  }

  updatePreferentialBid(data: any): Observable<any> {
    let bidId = data.bid_id;
    let complete_url = this.createCompleteUrl(
      `bids/${bidId}/update_preferential_bid`
    );
    return this.http.put<any>(complete_url, data);
  }

  allocateBid(bid_id: string): Observable<Trade[]> {
    const params = new HttpParams().set("bid_id", bid_id);
    let complete_url = this.createCompleteUrl("bids/allocations");
    return this.http.get<Trade[]>(complete_url, { params: params });
  }

  createFixRateBid(bidData: FixedRateBidRequest): Observable<FixedBidResponse> {
    return this.http.post<FixedBidResponse>(
      this.createCompleteUrl("bids/fix_rate"),
      bidData
    );
  }

  getFixRateBid(bidId: string): Observable<FixedBidResponse> {
    return this.http.get<FixedBidResponse>(
      this.createCompleteUrl(`bids/fix_rate/${bidId}`)
    );
  }

  completeFixedRateBid(
    bidId: string,
    isAccepted: boolean
  ): Observable<ContractResponse> {
    const queryParams = new HttpParams().set("is_accept", isAccepted);
    return this.http.put<ContractResponse>(
      this.createCompleteUrl(
        `bids/fix_rate/${bidId}/complete?${queryParams.toString()}`
      ),
      {}
    );
  }

  getActiveOpenBid(): Observable<BidResponse> {
    return this.http.get<BidResponse>(this.createCompleteUrl("bids/active"));
  }

  remindPartnerForPreferentialBid(bidId: string): Observable<void> {
    return this.http.put<void>(
      this.createCompleteUrl(`bids/fix_rate/${bidId}/remind_partner`),
      {}
    );
  }

  withdrawBid(bidId: string): Observable<any> {
    return this.http.put<any>(
      this.createCompleteUrl(`bids/${bidId}/withdraw`),
      null
    );
  }

  validatePreferentialBid(
    customer_id: number,
    start_date: string,
    end_date: string,
    energy_amount: number | string,
    price: number | string
  ): Observable<string> {
    const params: any = {
      customer_id,
      start_date,
      end_date,
      energy_amount,
      price,
    };
    return this.http.get<string>(
      this.createCompleteUrl("bids/validate/preferential_bid"),
      { params }
    );
  }

  validateNonPreferentialBid(
    bid_round_id: string,
    transaction_type_id: number,
    bid_price: number | string,
    bid_type: string
  ): Observable<string> {
    const params: any = {
      bid_round_id,
      transaction_type_id,
      bid_price,
      bid_type
    };
    return this.http.get<string>(
      this.createCompleteUrl("bids/validate/non-preferential"),
      { params }
    );
  }
}
